/**
 * Portal.tsx — External party portal at /portal/:token
 *
 * Public route (not inside <Gate>). No app nav. Swiss aesthetic, mobile-first.
 * Flow:
 *   1. Load /portal/:token/session  →  validate token
 *   2. If invalid → error state
 *   3. If ndaRequired && !ndaSignedAt → NDA gate (full-screen NDA + signature)
 *   4. After NDA → deal summary + documents + upload
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Stack, Row, Text, Button, Badge, Tag, MobileSheet } from '../ui';

const API = import.meta.env.VITE_API_URL || 'https://api.ardacious.app';

function portalFetch(token: string, path: string, opts?: RequestInit) {
  return fetch(`${API}/portal/${token}${path}`, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(opts?.headers ?? {}) },
  });
}

// ── NDA Gate ─────────────────────────────────────────────────────────────────

function NdaGate({
  token,
  party,
  onSigned,
}: {
  token: string;
  party: { signerName: string; signerEmail: string };
  onSigned: () => void;
}) {
  const [nda, setNda] = useState<{ body: string; hash: string } | null>(null);
  const [signerName, setSignerName] = useState(party.signerName);
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API}/portal/${token}/nda`)
      .then(r => r.json())
      .then(d => setNda(d))
      .catch(() => setErr('Could not load the NDA. Please refresh.'));
  }, [token]);

  async function sign() {
    if (!nda || !signerName.trim() || !agreed) return;
    setSigning(true); setErr(null);
    try {
      const r = await portalFetch(token, '/nda/sign', {
        method: 'POST',
        body: JSON.stringify({
          documentBody: nda.body,
          method: 'typed',
          signature: signerName.trim(),
          signerName: signerName.trim(),
          signerEmail: party.signerEmail || undefined,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      onSigned();
    } catch (e: any) {
      setErr(e.message ?? 'Signing failed. Please try again.');
    } finally {
      setSigning(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Slim portal header */}
      <div className="hairline-b px-4 h-11 flex items-center justify-between bg-bg sticky top-0 z-10">
        <Text variant="small" className="font-medium">inner circle · secure portal</Text>
        <Text variant="label" muted>NDA required</Text>
      </div>

      <div className="flex-1 max-w-2xl mx-auto px-4 py-6 w-full">
        <Stack gap={4}>
          <Stack gap={1}>
            <Text variant="display">Non-Disclosure Agreement</Text>
            <Text variant="small" muted>
              Please read and sign before accessing deal materials.
            </Text>
          </Stack>

          {!nda ? (
            <Text variant="label" muted>Loading NDA…</Text>
          ) : (
            <div className="hairline rounded-sm bg-whisper p-4 max-h-[50vh] overflow-y-auto">
              <div className="prose prose-sm max-w-none">
                {nda.body.split('\n').map((line, i) => {
                  if (line.startsWith('## ')) return <h3 key={i} className="text-sm font-semibold mt-3 mb-1">{line.slice(3)}</h3>;
                  if (line.startsWith('# ')) return <h2 key={i} className="text-base font-semibold mb-2">{line.slice(2)}</h2>;
                  if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="text-sm font-medium mb-1">{line.slice(2, -2)}</p>;
                  if (!line.trim()) return <div key={i} className="h-2" />;
                  return <p key={i} className="text-sm text-muted leading-relaxed mb-1">{line}</p>;
                })}
              </div>
            </div>
          )}

          <Stack gap={3}>
            <Stack gap={1}>
              <Text variant="micro">Your full legal name *</Text>
              <input
                type="text"
                value={signerName}
                onChange={e => setSignerName(e.target.value)}
                placeholder="e.g. Jane Smith"
                className="h-11 sm:h-9 hairline rounded-sm px-3 bg-bg text-sm w-full"
              />
            </Stack>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                className="mt-1 h-4 w-4 rounded"
              />
              <Text variant="small">
                I have read and agree to the terms of this Non-Disclosure Agreement. I understand this is a binding legal document.
              </Text>
            </label>

            {err && <Text variant="small" className="text-negative">{err}</Text>}

            <Button
              variant="primary"
              disabled={!nda || !agreed || !signerName.trim() || signing}
              onClick={sign}
              className="w-full sm:w-auto justify-center"
            >
              {signing ? 'Signing…' : 'Sign NDA and continue →'}
            </Button>
          </Stack>
        </Stack>
      </div>
    </div>
  );
}

// ── Document Viewer Sheet ─────────────────────────────────────────────────────

function DocumentViewer({
  token,
  doc,
  onClose,
}: {
  token: string;
  doc: { id: string; title: string; type: string } | null;
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!doc) { setUrl(null); return; }
    // Build a direct URL; browser handles rendering PDF/text/image inline
    setUrl(`${API}/portal/${token}/document/${doc.id}`);
  }, [doc, token]);

  return (
    <MobileSheet open={!!doc} onClose={onClose} title={doc?.title ?? ''} maxWidthMd="w-[680px]">
      {url && (
        <iframe
          src={url}
          className="w-full h-[70vh] sm:h-[80vh] rounded-sm border-0"
          title={doc?.title}
        />
      )}
    </MobileSheet>
  );
}

// ── Sign Document Modal ───────────────────────────────────────────────────────

function SignDocModal({
  token,
  doc,
  signerName: defaultName,
  onClose,
  onSigned,
}: {
  token: string;
  doc: { id: string; title: string } | null;
  signerName: string;
  onClose: () => void;
  onSigned: () => void;
}) {
  const [name, setName] = useState(defaultName);
  const [signing, setSigning] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!doc || !name.trim()) return;
    setSigning(true); setErr(null);
    try {
      const r = await portalFetch(token, `/document/${doc.id}/sign`, {
        method: 'POST',
        body: JSON.stringify({ signatureMethod: 'typed', signatureData: name.trim(), signerName: name.trim() }),
      });
      if (!r.ok) throw new Error(await r.text());
      onSigned();
      onClose();
    } catch (e: any) {
      setErr(e.message ?? 'Signing failed');
    } finally {
      setSigning(false);
    }
  }

  return (
    <MobileSheet open={!!doc} onClose={onClose} title={`Sign: ${doc?.title ?? ''}`}>
      <Stack gap={4}>
        <Text variant="small" muted>
          Type your full name below to apply your electronic signature to this document.
          Your signature is legally binding.
        </Text>
        <Stack gap={1}>
          <Text variant="micro">Full name (typed signature)</Text>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            className="h-11 sm:h-9 hairline rounded-sm px-3 bg-bg text-sm w-full"
            placeholder="Your full legal name"
          />
          {/* Signature preview */}
          {name.trim() && (
            <div className="hairline rounded-sm p-3 mt-1 bg-whisper">
              <span style={{ fontFamily: 'Georgia, serif', fontSize: '22px', color: '#1a1a1a', fontStyle: 'italic' }}>
                {name}
              </span>
            </div>
          )}
        </Stack>
        {err && <Text variant="small" className="text-negative">{err}</Text>}
        <Row gap={2} justify="end">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!name.trim() || signing} onClick={submit}>
            {signing ? 'Signing…' : 'Create signature'}
          </Button>
        </Row>
      </Stack>
    </MobileSheet>
  );
}

// ── Upload Zone ───────────────────────────────────────────────────────────────

function UploadZone({
  token,
  onUploaded,
}: {
  token: string;
  onUploaded: (doc: any) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  async function uploadFile(file: File) {
    setUploading(true); setErr(null);
    try {
      const r = await fetch(
        `${API}/portal/${token}/upload?filename=${encodeURIComponent(file.name)}&type=portal-upload`,
        {
          method: 'PUT',
          headers: { 'content-type': file.type || 'application/octet-stream', 'content-length': String(file.size) },
          body: file,
        }
      );
      if (!r.ok) throw new Error(await r.text());
      const d = await r.json();
      onUploaded({ id: d.artifactId, title: file.name, type: 'portal-upload', addedByName: d.addedByName, signatureStatus: 'none', hasMySignature: false, createdAt: new Date().toISOString() });
    } catch (e: any) {
      setErr(e.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <Stack gap={2}>
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) uploadFile(f); }}
        onClick={() => fileRef.current?.click()}
        className={`hairline rounded-sm p-6 text-center cursor-pointer transition-colors ${dragging ? 'bg-ink/5' : 'bg-whisper hover:bg-ink/5'}`}
      >
        <Text variant="small" muted>
          {uploading ? 'Uploading…' : 'Drop files here or click to upload'}
        </Text>
        <input ref={fileRef} type="file" className="hidden" onChange={e => e.target.files?.[0] && uploadFile(e.target.files[0])} />
      </div>
      {err && <Text variant="label" className="text-negative">{err}</Text>}
    </Stack>
  );
}

// ── Main Portal Page ──────────────────────────────────────────────────────────

function PortalPage({
  token,
  session,
  onNdaSigned,
}: {
  token: string;
  session: any;
  onNdaSigned: () => void;
}) {
  const [docs, setDocs] = useState<any[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [viewDoc, setViewDoc] = useState<any>(null);
  const [signDoc, setSignDoc] = useState<any>(null);
  const [parties, setParties] = useState<any[]>([]);

  const loadDocs = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const r = await portalFetch(token, '/documents');
      if (r.ok) {
        const d = await r.json();
        setDocs(d.documents ?? []);
      }
    } catch {} finally { setLoadingDocs(false); }
  }, [token]);

  useEffect(() => { if (session.ndaSignedAt) loadDocs(); }, [session.ndaSignedAt, loadDocs]);

  const expiresInDays = session.expiresAt
    ? Math.ceil((new Date(session.expiresAt).getTime() - Date.now()) / 86400000)
    : null;

  const sigStatusPill = (s: string) => {
    if (s === 'signed') return <Badge tone="positive">signed</Badge>;
    if (s === 'partial') return <Badge tone="warning">partial</Badge>;
    return <Badge tone="neutral">unsigned</Badge>;
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Portal header */}
      <div className="hairline-b px-3 sm:px-4 h-11 flex items-center justify-between bg-bg sticky top-0 z-10">
        <Row gap={2} align="center">
          <Text variant="small" className="font-medium">inner circle · secure portal</Text>
        </Row>
        <Row gap={2} align="center">
          <Text variant="label" muted className="hidden sm:inline">
            {session.party.contactName ?? session.party.legalName}
          </Text>
          {expiresInDays !== null && (
            <Text variant="label" muted>
              expires in {expiresInDays}d
            </Text>
          )}
        </Row>
      </div>

      <div className="flex-1 max-w-2xl mx-auto px-3 sm:px-4 py-4 sm:py-6 w-full">
        <Stack gap={6}>
          {/* 1. Deal summary */}
          <Stack gap={3}>
            <Stack gap={1}>
              <Text variant="display">{session.deal.name}</Text>
              <Row gap={2} wrap>
                {session.deal.type && <Tag>{session.deal.type}</Tag>}
                {session.deal.sector && <Tag>{session.deal.sector}</Tag>}
                {session.deal.geography && <Tag>{session.deal.geography}</Tag>}
                {session.deal.stage && <Badge tone="neutral">{session.deal.stage}</Badge>}
              </Row>
            </Stack>
            <div className="hairline rounded-sm p-3 bg-whisper">
              <Text variant="micro" muted>You are listed as:</Text>
              <Text variant="small" className="mt-0.5 font-medium">
                {session.party.legalName} · {session.party.role}
              </Text>
              {session.party.contactName && (
                <Text variant="label" muted>Contact: {session.party.contactName}</Text>
              )}
            </div>
          </Stack>

          {/* 2. Documents */}
          <Stack gap={3}>
            <Text variant="small" className="font-medium">Documents</Text>
            {!session.ndaSignedAt ? (
              <div className="hairline rounded-sm p-4 bg-whisper">
                <Text variant="small" muted>Sign the NDA above to access documents.</Text>
              </div>
            ) : loadingDocs ? (
              <Text variant="label" muted>Loading documents…</Text>
            ) : docs.length === 0 ? (
              <Text variant="label" muted>No documents yet.</Text>
            ) : (
              <Stack gap={2}>
                {docs.map(doc => (
                  <div key={doc.id} className="hairline rounded-sm p-3">
                    <Row justify="between" align="start">
                      <Stack gap={0.5} className="flex-1 min-w-0">
                        <Row gap={1} wrap>
                          <Text variant="small" className="font-medium truncate">{doc.title}</Text>
                          <Tag>{doc.type}</Tag>
                        </Row>
                        <Row gap={1} wrap>
                          {sigStatusPill(doc.signatureStatus)}
                          {doc.addedByName && <Text variant="label" muted>{doc.addedByName}</Text>}
                        </Row>
                      </Stack>
                      <Row gap={1} className="ml-2 flex-shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => setViewDoc(doc)}>View</Button>
                        {doc.signatureStatus !== 'signed' && !doc.hasMySignature && (
                          <Button variant="secondary" size="sm" onClick={() => setSignDoc(doc)}>Sign</Button>
                        )}
                        {doc.hasMySignature && (
                          <Text variant="label" className="text-positive">✓ signed</Text>
                        )}
                      </Row>
                    </Row>
                  </div>
                ))}
              </Stack>
            )}
          </Stack>

          {/* 3. Upload */}
          {session.ndaSignedAt && session.scopes?.includes('upload') && (
            <Stack gap={3}>
              <Text variant="small" className="font-medium">Upload documents</Text>
              <Text variant="label" muted>
                Files you upload will be visible to the deal team.
              </Text>
              <UploadZone
                token={token}
                onUploaded={doc => setDocs(prev => [doc, ...prev])}
              />
            </Stack>
          )}
        </Stack>
      </div>

      {/* NDA gate overlay (if not signed yet) */}
      {!session.ndaSignedAt && (
        <div className="fixed inset-0 z-20 bg-bg/95 backdrop-blur-sm flex flex-col">
          <NdaGate
            token={token}
            party={{ signerName: session.party.contactName ?? '', signerEmail: session.party.contactEmail ?? '' }}
            onSigned={onNdaSigned}
          />
        </div>
      )}

      {/* Document viewer */}
      <DocumentViewer token={token} doc={viewDoc} onClose={() => setViewDoc(null)} />

      {/* Sign document modal */}
      <SignDocModal
        token={token}
        doc={signDoc}
        signerName={session.party.contactName ?? ''}
        onClose={() => setSignDoc(null)}
        onSigned={loadDocs}
      />
    </div>
  );
}

// ── Route Component ───────────────────────────────────────────────────────────

export function Portal() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<'loading' | 'error' | 'valid'>('loading');
  const [errorReason, setErrorReason] = useState<string>('invalid');
  const [session, setSession] = useState<any>(null);

  const loadSession = useCallback(async () => {
    if (!token) { setState('error'); return; }
    try {
      const r = await fetch(`${API}/portal/${token}/session`);
      const d = await r.json();
      if (!r.ok || !d.ok) {
        setErrorReason(d.reason ?? 'invalid');
        setState('error');
      } else {
        setSession(d);
        setState('valid');
      }
    } catch {
      setErrorReason('invalid');
      setState('error');
    }
  }, [token]);

  useEffect(() => { loadSession(); }, [loadSession]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Text variant="small" muted>Loading…</Text>
      </div>
    );
  }

  if (state === 'error') {
    const messages: Record<string, string> = {
      revoked: 'This link has been revoked by the deal team.',
      expired: 'This link has expired.',
      invalid: 'This link is not valid.',
    };
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-sm w-full hairline rounded-sm p-6 text-center">
          <Stack gap={3}>
            <Text variant="display" className="text-negative">Link not valid</Text>
            <Text variant="small" muted>
              {messages[errorReason] ?? messages['invalid']}{' '}
              Contact the person who shared this link with you.
            </Text>
          </Stack>
        </div>
      </div>
    );
  }

  return (
    <PortalPage
      token={token!}
      session={session}
      onNdaSigned={() => {
        // Re-load session to get updated ndaSignedAt
        loadSession();
      }}
    />
  );
}
